import { EntityType, SearchFilterOptions } from "types";
import { useUserConfig } from "hooks/useUserConfig";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { useEntityType } from "hooks/useEntityType";
import * as Features from "helpers/constants/filters";

/**
 * Replaces getIsSearchByRelevanceEnabled. Entity-type-aware semantic search relevance check.
 */
export function useSearchByRelevanceEnabled(): boolean {
  const entityType = useEntityType();
  const featureKey =
    entityType === "OBJECTS"
      ? Features.TEXTUAL_SEARCH_RELEVANCE_SCORE_OBJECTS
      : Features.TEXTUAL_SEARCH_RELEVANCE_SCORE_IMAGES;
  return useFeatureFlagService(featureKey).enabled;
}

/**
 * Replaces getSearchOptions. Entity-type-aware search config.
 * When entityType is provided, uses that; otherwise uses the current entity type from context.
 */
export function useSearchOptions(
  entityType?: EntityType | null,
): SearchFilterOptions {
  const contextEntityType = useEntityType();
  const resolvedType = entityType ?? contextEntityType;
  const { config } = useUserConfig();

  const featureKey =
    resolvedType === "OBJECTS"
      ? Features.TEXTUAL_SEARCH_OBJECT
      : Features.TEXTUAL_SEARCH_IMAGE;

  const feature = config?.find((f) => f.feature_key === featureKey);

  return {
    labelsSearch: !!feature?.featureOptions?.labels_search,
    captionsSearch: !!feature?.featureOptions?.captions_search,
    semanticSearch: !!feature?.featureOptions?.semantic_search,
    enabled: feature?.feature_behavior["ANY"] === "SHOW",
  };
}

/**
 * Replaces getIsPromptSupportOptions.
 */
export function usePromptSupportOptions(): {
  enabled: boolean;
  maxChars: number;
} {
  const { enabled, featureOptions } = useFeatureFlagService(
    Features.PROMPT_SUPPORT_ENABLED,
  );
  const maxChars =
    typeof featureOptions?.vqa_prompt_max_size === "number"
      ? featureOptions.vqa_prompt_max_size
      : 300;
  return { enabled, maxChars };
}

/**
 * Replaces getIsMislabelesThresholdEnabled. Entity-type-aware.
 */
export function useMislabelThresholdEnabled(): boolean {
  const entityType = useEntityType();
  const featureKey =
    entityType === "OBJECTS"
      ? Features.MISLABEL_CONFIDENCE_FILTER_FOR_OBJECTS
      : Features.MISLABEL_CONFIDENCE_FILTER_FOR_IMAGES;
  return useFeatureFlagService(featureKey).enabled;
}

/**
 * Replaces getMislabelsThresholdRange. Entity-type-aware.
 */
export function useMislabelThresholdRange(): [number, number] | null {
  const entityType = useEntityType();
  const featureKey =
    entityType === "OBJECTS"
      ? Features.MISLABEL_CONFIDENCE_FILTER_FOR_OBJECTS
      : Features.MISLABEL_CONFIDENCE_FILTER_FOR_IMAGES;

  const { enabled, featureOptions } = useFeatureFlagService(featureKey);

  if (!enabled || !featureOptions) return null;

  const threshold = Object.values(featureOptions) as number[];
  return threshold.length === 2 ? (threshold as [number, number]) : null;
}

/**
 * Replaces getRelevanceScores. Entity-type-aware.
 */
export function useRelevanceScores(): number[] {
  const entityType = useEntityType();
  const searchOptions = useSearchOptions();

  const featureKey =
    entityType === "OBJECTS"
      ? Features.TEXTUAL_SEARCH_RELEVANCE_SCORE_OBJECTS
      : Features.TEXTUAL_SEARCH_RELEVANCE_SCORE_IMAGES;

  const { featureOptions } = useFeatureFlagService(featureKey);

  if (!searchOptions.semanticSearch) return [];

  return (
    (featureOptions?.TEXTUAL_SEARCH_RELEVANCE_SCORE_THRESHOLDS as number[]) ||
    []
  );
}

/**
 * Replaces getLabelPropagationFeatureOptions.
 */
export function useLabelPropagationOptions(): {
  required_seed_samples_per_label: number;
} {
  const { featureOptions } = useFeatureFlagService(Features.FLYWHEEL);
  return (featureOptions || {}) as {
    required_seed_samples_per_label: number;
  };
}
